import { Edit, Print } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { Supply } from '../../apiTypes';
import { Column } from '../../components/DataTable';
import DataTablePaginated from '../../components/DataTablePaginated';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { usePagination } from '../../utils/api';
import { dateLocaleMap } from '../../utils/dates';
import SupplyAcceptModal from './SupplyAcceptModal';
import SupplyEditModal from './SupplyEditModal';
import SupplyPrintModal from './SupplyPrintModal';

type Row = DbDocument<Supply>;

const statusMap: Record<Supply['status'], keyof typeof t> = {
  active: 'in_progress',
  closed: 'accepted',
  deleted: 'cancelled',
};

const SupplyList = () => {
  const { cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { worker } = useCafe();
  const [editSupply, setEditSupply] = useState<Row | null>(null);
  const [openPrint, setOpenPrint] = useState(false);
  const [openAccept, setOpenAccept] = useState(false);
  const [open, setOpen] = useState(false);

  const {
    page,
    onPageChange,
    data: supplies,
    isLoading: isSuppliesLoading,
    revalidate,
    count,
  } = usePagination<Supply>({
    path: `cafes/${cafeSlug}/supply`,
    orderByField: 'time',
  });

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'date',
        title: t.date[lang],
        align: 'left',
        render: (r: Row) => <Typography>{format(r.time, 'dd MMMM', { locale: dateLocaleMap[lang] })}</Typography>,
      },
      {
        name: 'number',
        align: 'left',
        title: t.number[lang],
        render: (r: Row) => <Typography>{r.number}</Typography>,
      },
      {
        name: 'provider',
        align: 'left',
        title: t.provider[lang],
        render: (r: Row) => <Typography>{r.provider}</Typography>,
        filterable: true,
      },
      {
        name: 'status',
        align: 'left',
        title: t.status[lang],
        render: (r: Row) => (
          <Typography
            color={(t) => {
              if (r.status === 'active') {
                return t.palette.info.main;
              }
              if (r.status === 'closed') {
                return t.palette.success.main;
              }
              return t.palette.error.main;
            }}
          >
            {t[statusMap[r.status]][lang]}
          </Typography>
        ),
      },
      {
        name: 'accept',
        title: '',
        render: (r: Row) =>
          r.status === 'active' && (
            <PrimaryButton
              color="secondary"
              variant="outlined"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEditSupply(r);
                setOpenAccept(true);
              }}
              disabled={!worker?.credentials.accept_supplies}
            >
              {t.accept[lang]}
            </PrimaryButton>
          ),
      },
      {
        name: 'edit',
        title: '',
        render: (r: Row) =>
          r.status === 'active' && (
            <IconButton
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setEditSupply(r);
                setOpen(true);
              }}
              disabled={!worker?.credentials.edit_supplies}
            >
              <Edit />
            </IconButton>
          ),
      },
      {
        name: 'print',
        title: '',
        render: (r: Row) => (
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setEditSupply(r);
              setOpenPrint(true);
            }}
          >
            <Print />
          </IconButton>
        ),
      },
    ],
    [lang, worker?.credentials.accept_supplies, worker?.credentials.edit_supplies]
  );

  return (
    <Box sx={{ maxWidth: 800, m: '0 auto', pb: 2 }}>
      <DataTablePaginated
        columns={columns}
        loading={isSuppliesLoading}
        rows={supplies || []}
        onRowClick={(r) => {
          setEditSupply(r);
          setOpen(true);
        }}
        actionButton={
          <PrimaryButton
            onClick={() => setOpen(true)}
            variant="contained"
            disabled={!worker?.credentials.create_supplies}
          >
            {t.create_supply[lang]}
          </PrimaryButton>
        }
        page={page}
        count={count}
        onPageChange={onPageChange}
      />

      {open && (
        <SupplyEditModal
          onClose={() => {
            setOpen(false);
            setEditSupply(null);
          }}
          onSubmit={revalidate}
          supply={editSupply}
        />
      )}

      {openAccept && (
        <SupplyAcceptModal
          onClose={() => {
            setOpenAccept(false);
            setEditSupply(null);
          }}
          onSubmit={revalidate}
          supply={editSupply}
        />
      )}

      {openPrint && (
        <SupplyPrintModal
          onClose={() => {
            setOpenPrint(false);
            setEditSupply(null);
          }}
          supply={editSupply}
        />
      )}
    </Box>
  );
};

export default SupplyList;
